import type { ProfileCardSpan } from '@/components/common/ProfileCardGrid';
import type { StartupCompletionField } from '@/lib/types/startup-completion.types';
import type { StartupProfileFormValues } from './schema';
import { STARTUP_PROFILE_STRINGS as T } from './startup-profile.strings';

/** The startup profile's editable cards (FR-172). */
export type StartupSectionId = 'company' | 'financials' | 'raise' | 'team' | 'pitch';

export interface StartupSectionConfig {
  title: string;
  /** Form fields the section's side panel edits; only these are validated (FR-167). */
  fields: readonly (keyof StartupProfileFormValues)[];
  span: ProfileCardSpan;
}

export const STARTUP_SECTIONS: Record<StartupSectionId, StartupSectionConfig> = {
  company: {
    title: T.COMPANY_DETAILS,
    fields: [
      'logoFileKey',
      'companyName',
      'briefDescription',
      'sectorId',
      'subsectorIds',
      'sectorOther',
      'fundingStage',
      'yearFounded',
      'employeeCount',
      'hqCountryId',
      'hqStateId',
      'hqCityId',
      'hqCityName',
      'recentMilestone',
      'companyWebsiteUrl',
      'companyLinkedinUrl',
    ],
    span: 5,
  },
  financials: {
    title: T.FINANCIALS,
    fields: [
      'totalRevenueRange',
      'arr',
      'amountRaisedToDate',
      'nondilutiveFunding',
      'aiClassification',
      'revenueModel',
      'revenueGrowthRate',
      'payingCustomers',
    ],
    span: 12,
  },
  raise: {
    title: T.CURRENT_RAISE,
    fields: [
      'financeSoughtLow',
      'financeSoughtHigh',
      'instrument',
      'targetCloseDate',
      'valuationOrCap',
      'hasLeadInvestor',
      'leadInvestorName',
      'useOfFunds',
      'investorTypesSought',
    ],
    span: 7,
  },
  pitch: {
    title: T.PITCH_MATERIALS,
    fields: ['pitchDeckFileKey', 'pitchDeckFileSizeBytes', 'pitchVideoUrl'],
    span: 5,
  },
  team: {
    title: T.FOUNDING_TEAM,
    fields: [
      'foundingTeam',
      'founderFemale',
      'founderUnderrepresented',
      'founderUnderrepresentedCategories',
    ],
    span: 7,
  },
};

/**
 * Card order on the page, as in the client-approved mockup: Company details
 * beside Current raise, Founding team beside Pitch materials. Financials has
 * no card in the mockup (its numbers sat in tiles the Option B header
 * replaced), so it takes the full-width row underneath.
 */
export const STARTUP_SECTION_ORDER: readonly StartupSectionId[] = [
  'company',
  'raise',
  'team',
  'pitch',
  'financials',
];

/**
 * Which card fixes each completion item (FR-171). Exhaustive, so a new
 * completion field fails the build until it is mapped (Flow 2). `null` means
 * no profile form edits it today: `marketModels` is collected only by the
 * create wizard, so the menu lists it without a link.
 */
export const STARTUP_COMPLETION_SECTION: Record<StartupCompletionField, StartupSectionId | null> = {
  companyName: 'company',
  briefDescription: 'company',
  aiClassification: 'financials',
  fundingStage: 'company',
  amountRaisedToDate: 'financials',
  yearFounded: 'company',
  hqCountryId: 'company',
  hqStateId: 'company',
  companyWebsiteUrl: 'company',
  pitchDeckUrl: 'pitch',
  totalRevenueRange: 'financials',
  revenueModel: 'financials',
  revenueGrowthRate: 'financials',
  payingCustomers: 'financials',
  financeSoughtLow: 'raise',
  financeSoughtHigh: 'raise',
  instrument: 'raise',
  sectors: 'company',
  foundingTeam: 'team',
  marketModels: null,
  investorTypes: 'raise',
};
